import * as React from 'react';
import * as _ from 'underscore';

import { MetricCardWithTimeSeries } from '@tableau/metrics-shared-widgets-dom/lib/src/MetricCardWithTimeSeries';
import { Messages } from '../../../codegen/Localize';
import { FavoriteToggle } from '../../../common/react/components/FavoriteToggle/FavoriteToggle';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { getMetricMeasure, metricCardProps } from '../../../metrics';
import { L10n } from '../../../ts/L10n/L10n';
import { IMetricDataInfo, isMetricInfo } from '../../../ts/models/IMetricInfo';
import { IExplorableItemTypeInfo } from '../../../ts/models/resourceInfoParts';
import { ConnectedMetricTooltip } from '../Tooltips/MetricTooltip';
import { TooltipAnchor } from '../Tooltips/TooltipAnchor';
import { CardViewStyles } from './CardViewStyles';
import { ContentCard, ContentCardProps, getContentUrlForItem } from './ContentCard';
import { DetailsTooltipStyles } from './DetailsTooltipStyles';
import { Thumbnail } from './Thumbnail';

interface MetricCardState {
  readonly metricData?: IMetricDataInfo;
}

export interface MetricCardOwnProps extends ContentCardProps {
}

export interface MetricCardStateProps {
  readonly metricData: IMetricDataInfo;
}

export type MetricCardProps = MetricCardOwnProps & MetricCardStateProps;

export class MetricCard extends ContentCard<MetricCardProps, MetricCardState> {
  public state: MetricCardState = {};

  private renderTimerHandle?: ReturnType<typeof setTimeout>;

  public render() {
    const showLastAccessedAt = this.props.inRecentsSpace || this.props.showLastAccessedAt;

    return (
      <div className={CardViewStyles.Card}>
        <TooltipAnchor
          tooltipContent={this.renderTooltipContent}
          width={DetailsTooltipStyles.Width}
          onShowTooltip={this.handleShowTooltip}
          title={Messages.metricDetails()}
          disableAutoFocus={this.props.content.owner && this.props.content.owner.isHiddenInternalUser}
          disableTooltip={this.props.disableTooltip}
        >
          {this.renderMetricCard()}
        </TooltipAnchor>
        <div className={CardViewStyles.ActionRow}>
          <span className={CardViewStyles.ActionRowName} title={this.props.content.name}>
            {L10n.elideIfLong(this.props.content.name)}
          </span>
          <span>
            <FavoriteToggle canHaveKeyboardFocus={true}
              isFavorite={this.props.content.favorite}
              onAddFavorite={() => this.props.addContentFavorite({ ...this.props.content, contentType: 'metric'})}
              onRemoveFavorite={() => this.props.removeContentFavorite({ ...this.props.content, contentType: 'metric'})} />
          </span>
          { this.renderActionsMenu() }
        </div>
        {
        showLastAccessedAt ? (
          <div className={CardViewStyles.CardLastAccessedAt}>
            {this.getLastAccessedAt()}
          </div>
        ) : (
          <div className={CardViewStyles.CardDescription}>
            {this.getDescription()}
          </div>
        )
        }
      </div>
    );
  }

  private renderTooltipContent = () => {
    const timestamp = this.state.metricData ? metricCardProps(this.state.metricData, isMetricInfo(this.props.content) && this.props.content).lastUpdatedTimestamp : undefined;
    return <ConnectedMetricTooltip content={this.props.content}
                                   urls={this.props.urls}
                                   lastUpdatedTimestamp={timestamp}
                                   inProjectSpace={this.props.inProjectSpace}/>;
  }

  public componentDidUpdate(): void {
    this.delayAddingDataToState(this.state, this.props.content);
  }

  public componentDidMount(): void {
    this.delayAddingDataToState(this.state, this.props.content);
  }

  public componentWillUnmount() {
    if (this.renderTimerHandle) {
      clearTimeout(this.renderTimerHandle);
    }
  }

  private delayAddingDataToState(state: MetricCardState, content: IExplorableItemTypeInfo): void {
    if (isMetricInfo(content) && this.props.metricData !== state.metricData) {
    this.renderTimerHandle = setTimeout(() => this.setState({metricData: this.props.metricData}),
      Math.floor(Math.random() * 10));
    }
  }

  private renderMetricCard() {
    if (this.haveMetricDataPoints()) {
      const containerClasses = this.props.hoveredOrSelected && this.haveMetricDataPoints() ? [CardViewStyles.ThumbnailPreloaded, CardViewStyles.CardHighlight]
        : [CardViewStyles.ThumbnailPreloaded];

      let metricCardProperties = metricCardProps(this.state.metricData, isMetricInfo(this.props.content) && this.props.content);
      if (this.props.forDragPreview) {
        // when rendering the card just for a drag preview, don't show the sparkline because it makes the preview slower
        metricCardProperties.data = getMetricMeasure(this.state.metricData);
      }

      return (
        <div className={containerClasses.join(' ')} >
          <div className={CardViewStyles.SelectOverlay}></div>
          <a href={getContentUrlForItem(this.props.content, this.props.urls)} style={{ textDecoration: 'none' }}>
            {<MetricCardWithTimeSeries {...metricCardProperties} />}
          </a>
        </div>
      );
    } else {
      return (
        <Thumbnail
          defaultImageUrl={imgPath('Spaces_Empty_Metrics.svg')}
          hoveredOrSelected={this.props.hoveredOrSelected}
          navigateUrl={getContentUrlForItem(this.props.content, this.props.urls)}
          useBase64EncodedImage={true}
        />
      );
    }
  }


  private haveMetricDataPoints(): boolean {
    return isMetricInfo(this.props.content) && this.state.metricData && !_.isUndefined(this.state.metricData.dataPoints);
  }
}
